/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist, important } from 'src/core/themes/luisStylist';
import { InformationCard } from 'src/luis/projects/analytics/components/TrainingEvaluation/InformationCard';
import { useHighlightedRecommendationsDecider } from 'src/luis/projects/analytics/hooks/useHighlightedRecommendationsDecider';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { LuisId } from 'src/luis/types/Resource';

export type RecommendationsProps = {
    /**
     * The selected faulty intent.
     */
    selectedIntent: IntentMetadata;

    /**
     * A callback to load some intent trining result if needed from the backend.
     */
    loadIntentTrainingResult: (intentId: LuisId) => void;
};

const messages = defineMessages({
    title: {
        id: 'analytics.intentsWithErrorsCard.title',
        defaultMessage: 'HOW TO IMPROVE INTENTS'
    },
    dataImbalanceRecommendationTitle: {
        id: 'analytics.intentsWithErrorsCard.dataImbalanceRecommendationTitle',
        defaultMessage: 'This intent contains fewer example utterances relative to other intents.'
    },
    dataImbalanceRecommendation: {
        id: 'analytics.intentsWithErrorsCard.dataImbalanceRecommendation',
        defaultMessage: 'Add more utterances to this intent.'
    },
    addPatternsRecommendationTitle: {
        id: 'analytics.intentsWithErrorsCard.addPatternsRecommendationTitle',
        defaultMessage: 'This intent and its nearest intent(s) use similar words.'
    },
    addPatternsRecommendationOne: {
        id: 'analytics.intentsWithErrorsCard.addPatternsRecommendationOne',
        defaultMessage:
            'Add more utterances to this intent using those words. This will reduce the weight of those words towards other intents.'
    },
    addPatternsRecommendationTwo: {
        id: 'analytics.intentsWithErrorsCard.addPatternsRecommendationTwo',
        defaultMessage: 'If two intents need to use similar words, but in different order, consider adding a pattern.'
    },
    mergingRecommendationTitle: {
        id: 'analytics.intentsWithErrorsCard.mergingRecommendationTitle',
        defaultMessage: 'This intent and its nearest intent(s) have similar words.'
    },
    mergingRecommendation: {
        id: 'analytics.intentsWithErrorsCard.mergingRecommendation',
        defaultMessage: `Consider merging this intent with one of its competitors. For example, “Order Sandwich” and
             “Order Pizza” might be combined into a broader intent called “Order Lunch”.`
    },
    splittingRecommendationTitle: {
        id: 'analytics.intentsWithErrorsCard.splittingRecommendationTitle',
        defaultMessage: 'This intent and its nearest intents(s) have similar words.'
    },
    splittingRecommendation: {
        id: 'analytics.intentsWithErrorsCard.splittingRecommendation',
        defaultMessage: 'Consider splitting unclear predictions into separated intent(s).'
    }
});

const { styleComponent, getClassName, styleUlist, styleLi } = getStylist('Dashboard');
const dashboardTheme = getScopedTheme('Dashboard');

const Title = styleComponent<ITextProps>(Text)('Title', theme => ({
    textTransform: 'uppercase',
    color: theme.defaultTextColor,
    marginBottom: theme.titleSpacing,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing,
    fontWeight: theme.boldFont as Property.FontWeight
}));

/**
 * Displays the suitable recommendations with highlighting mode and others are dimmed
 * to help improve the performance of the selected intent.
 *
 * @param props The properties to determine the right recommendations.
 */
export const Recommendations = observer((props: RecommendationsProps) => {
    const { selectedIntent, loadIntentTrainingResult } = props;
    const { localize } = useLocalization();

    const { needMerging, needPatterns, needSplitting, isDataImbalanced } = useHighlightedRecommendationsDecider(
        selectedIntent,
        loadIntentTrainingResult
    );

    return (
        <Stack tokens={{ padding: dashboardTheme.detailedFaultyIntentPercentLegendSpacing }} verticalAlign="center">
            <Title variant="smallPlus">{localize(messages.title)}</Title>
            <Stack tokens={{ childrenGap: 4 }}>
                <DataImbalanceRecommendation isDimmed={!isDataImbalanced} />
                <AddPatternsRecommendation isDimmed={!needPatterns} />
                <MergingRecommendation isDimmed={!needMerging} />
                <SplittingRecommendation isDimmed={!needSplitting} />
            </Stack>
        </Stack>
    );
});

const dimmedCardTheme = getClassName('DimmedCard', theme =>
    important({
        border: theme.dropdownBorder,
        backgroundColor: 'transparent',
        color: theme.statsComparisonColor,
        letterSpacing: theme.xLargeLetterSpacing
    })
);

const titleClass = getClassName('titleClass', theme => ({
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing
}));

const ShiftedUList = styleUlist('ShiftedUList', theme => ({
    margin: 0,
    listStyle: 'unset',
    paddingInlineStart: theme.listItemLeftShifting
}));

const TailedListItem = styleLi('TailedListItem', theme => ({
    marginBottom: theme.intentListItemSpacing
}));

/**
 * Displays the recommendations for the data imbalance problem either in highlighting mode
 * or not depending on the given input.
 *
 * @param props The properties to determine the recommendation mode.
 */
const DataImbalanceRecommendation = ({ isDimmed = true }: { isDimmed?: boolean }) => {
    const { localize } = useLocalization();

    return (
        <InformationCard
            titleClassName={titleClass}
            className={isDimmed ? dimmedCardTheme : null}
            title={localize(messages.dataImbalanceRecommendationTitle)}>
            <ShiftedUList>
                <TailedListItem>{localize(messages.dataImbalanceRecommendation)}</TailedListItem>
            </ShiftedUList>
        </InformationCard>
    );
};

/**
 * Displays the recommendations for the similar words problem either in highlighting mode
 * or not depending on the given input.
 *
 * @param props The properties to determine the recommendation mode.
 */
const AddPatternsRecommendation = ({ isDimmed = true }: { isDimmed?: boolean }) => {
    const { localize } = useLocalization();

    return (
        <InformationCard
            titleClassName={titleClass}
            className={isDimmed ? dimmedCardTheme : null}
            title={localize(messages.addPatternsRecommendationTitle)}>
            <ShiftedUList>
                <TailedListItem>{localize(messages.addPatternsRecommendationOne)}</TailedListItem>
                <TailedListItem>{localize(messages.addPatternsRecommendationTwo)}</TailedListItem>
            </ShiftedUList>
        </InformationCard>
    );
};

/**
 * Displays the recommendations for the merging problem either in highlighting mode
 * or not depending on the given input.
 *
 * @param props The properties to determine the recommendation mode.
 */
const MergingRecommendation = ({ isDimmed = true }: { isDimmed?: boolean }) => {
    const { localize } = useLocalization();

    return (
        <InformationCard
            titleClassName={titleClass}
            className={isDimmed ? dimmedCardTheme : null}
            title={localize(messages.mergingRecommendationTitle)}>
            <ShiftedUList>
                <TailedListItem>{localize(messages.mergingRecommendation)}</TailedListItem>
            </ShiftedUList>
        </InformationCard>
    );
};

/**
 * Displays the recommendations for the splitting problem either in highlighting mode
 * or not depending on the given input.
 *
 * @param props The properties to determine the recommendation mode.
 */
const SplittingRecommendation = ({ isDimmed = true }: { isDimmed?: boolean }) => {
    const { localize } = useLocalization();

    return (
        <InformationCard
            titleClassName={titleClass}
            className={isDimmed ? dimmedCardTheme : null}
            title={localize(messages.splittingRecommendationTitle)}>
            <ShiftedUList>
                <TailedListItem>{localize(messages.splittingRecommendation)}</TailedListItem>
            </ShiftedUList>
        </InformationCard>
    );
};
